<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { useConfirm } from 'primevue/useconfirm';
import {
  useContractManagement,
  type ContractFormData,
  type ContractPatchData,
} from 'src/composables/useContractManagement';
import SharedSplitPane from 'src/components/shared/SharedSplitPane.vue';
import ContractListTable from './ContractListTable.vue';
import ContractDetailPanel from './ContractDetailPanel.vue';
import ContractCreateDialog from './ContractCreateDialog.vue';
import type { FetchContractResponse } from 'src/api/models/fetchContractResponse';
import { FetchContractResponseStatus } from 'src/api/models/fetchContractResponseStatus';

const STATUS_OPTIONS = [
  { label: 'All', value: null },
  ...Object.values(FetchContractResponseStatus).map((s) => ({ label: s, value: s })),
];

const {
  contracts,
  loading,
  page,
  totalPages,
  totalRecords,
  searchQuery,
  statusFilter,
  selectedContractId,
  selectedContract,
  selectedContractLoading,
  operationInProgress,
  loadContracts,
  selectContract,
  createContractWithActionItems,
  updateContract,
  updateContractNote,
  saveContractEdits,
  activateContract,
  offerContract,
  searchOrganisations,
  searchUnits,
  searchBranches,
  searchContactPersons,
  searchRecipes,
} = useContractManagement();

const confirm = useConfirm();

const detailPanelRef = ref<InstanceType<typeof ContractDetailPanel> | null>(null);
const splitPaneRef = ref<InstanceType<typeof SharedSplitPane> | null>(null);
const showCreateDialog = ref(false);

const debouncedSearch = useDebounceFn(() => loadContracts(1), 300);
watch(searchQuery, () => debouncedSearch());

onMounted(() => {
  loadContracts();
});

function openContract(id: string | null) {
  const isOpening = !selectedContractId.value;
  selectContract(id);
  if (isOpening) splitPaneRef.value?.setInitialOpenPercent();
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function onRowClick(contract: FetchContractResponse) {
  const targetId = contract.id ?? null;
  if (targetId === selectedContractId.value) return;
  if (detailPanelRef.value?.isDirty) {
    confirm.require({
      message: 'U heeft onopgeslagen wijzigingen. Verwijderen?',
      header: 'Onopgeslagen wijzigingen',
      icon: 'pi pi-exclamation-triangle',
      acceptClass: 'p-button-danger',
      acceptLabel: 'Verwijderen',
      rejectLabel: 'Terug',
      accept: () => openContract(targetId),
    });
    return;
  }
  openContract(targetId);
}

function onPageChange(targetPage: number) {
  loadContracts(targetPage);
}

async function onUpdateContract(id: string, data: ContractPatchData) {
  const success = await updateContract(id, data);
  if (success) detailPanelRef.value?.clearDirty();
}

async function onUpdateNote(id: string, note: string) {
  const success = await updateContractNote(id, note);
  if (success) detailPanelRef.value?.clearDirty();
}

async function onSaveEdit(id: string, data: ContractFormData) {
  const success = await saveContractEdits(id, data);
  if (success) {
    detailPanelRef.value?.clearDirty();
    detailPanelRef.value?.exitEditMode();
  }
}

async function onCreateContract(data: ContractFormData) {
  const newId = await createContractWithActionItems(data);
  if (newId) showCreateDialog.value = false;
}

function onActivateContract(id: string) {
  activateContract(id);
}

function onOfferContract(id: string) {
  offerContract(id);
}
</script>

<template>
  <div class="flex flex-col h-full">
    <Toolbar class="border-none border-b surface-ground px-4 py-3">
      <template #start>
        <div class="flex items-center gap-3">
          <!-- <InputText
            data-testid="contract-search"
            v-model="searchQuery"
            placeholder="Zoek op nummer, organisatie, unit..."
            class="w-72"
            size="small"
          /> -->
          <!-- <Select
            data-testid="contract-status-filter"
            v-model="statusFilter"
            :options="STATUS_OPTIONS"
            option-label="label"
            option-value="value"
            placeholder="Status"
            size="small"
            class="w-40"
            @change="loadContracts(1)"
          /> -->
          <span v-if="totalRecords > 0" class="text-sm text-color-secondary">{{ totalRecords }} contracten</span>
        </div>
      </template>
      <template #end>
        <Button
          data-testid="contract-create-btn"
          icon="pi pi-plus"
          label="Nieuw contract"
          severity="success"
          size="small"
          @click="showCreateDialog = true"
        />
      </template>
    </Toolbar>

    <SharedSplitPane
      ref="splitPaneRef"
      storage-key="contract-split-percent"
      :detail-open="!!selectedContractId"
      class="mt-2"
    >
      <template #list>
        <ContractListTable
          :contracts="contracts"
          :loading="loading"
          :page="page"
          :total-pages="totalPages"
          :total-records="totalRecords"
          :selected-contract-id="selectedContractId"
          :compact="!!selectedContractId"
          @row-click="onRowClick"
          @page-change="onPageChange"
        />
      </template>
      <template #detail>
        <ContractDetailPanel
          ref="detailPanelRef"
          :contract="selectedContract"
          :loading="selectedContractLoading"
          :operation-in-progress="operationInProgress"
          :search-organisations-fn="searchOrganisations"
          :search-units-fn="searchUnits"
          :search-branches-fn="searchBranches"
          :search-contact-persons-fn="searchContactPersons"
          :search-recipes-fn="searchRecipes"
          @update-contract="onUpdateContract"
          @update-note="onUpdateNote"
          @save-edit="onSaveEdit"
          @activate-contract="onActivateContract"
          @offer-contract="onOfferContract"
          @close="selectContract(null)"
        />
      </template>
    </SharedSplitPane>

    <ContractCreateDialog
      :visible="showCreateDialog"
      :disabled="operationInProgress"
      :search-organisations-fn="searchOrganisations"
      :search-units-fn="searchUnits"
      :search-branches-fn="searchBranches"
      :search-contact-persons-fn="searchContactPersons"
      :search-recipes-fn="searchRecipes"
      @update:visible="showCreateDialog = $event"
      @submit="onCreateContract"
    />
  </div>
</template>
